import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks';
import { Table } from './Table';
import { ClientDataFixedColumns } from '../constants/ClientDataFixedColumns';
import { compoundCaseToTitleCase } from '../utils/stringUtils';
import { setSelectedComponentId } from '../ducks/pricingSlice';
import { openDialog } from '../ducks/dialogSlice';
import { Dialogs } from '../constants/Dialogs';
import { DisplayColumns, PricingCalculationColumns, RegionPricingColumns } from '../constants/ComponentPricing';
import { I18nKeys } from '../constants/I18nKeys';
import { areDiffValuesDifferent, getRowDiffColumnToFromValues } from '../utils/clientDataUtils';
import { ComponentCategoryItem } from '../types/ComponentPricing';
import {
  filterComponentCategoryItems,
  getDefaultParsedComponentPricingColumnValue,
} from '../utils/componentPricingUtils';
import { useComponentPricingRepo } from '../hooks/useComponentPricingRepo';
import { SortProperty } from '../types/SortProperty';
import { sortRows } from '../utils/sortUtils';

const useStyles = makeStyles({
  content: {
    flex: 1,
    minHeight: 0,
  },
  tableGrid: {
    height: '100%',
  },
  modified: {
    backgroundColor: '#E4A76733 !important',
  },
  cell: {
    '&:first-child': {
      paddingLeft: '32px',
    },
    '&:last-child': {
      paddingRight: '32px',
    },
  },
});

type Props = {
  searchValue?: string;
};

export const PricingComponentCategoryItems: React.FC<Props> = ({ searchValue = '' }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { componentCategoryItems = [], componentCategoryItemDiffs = [] } = useComponentPricingRepo({
    useComponentCategoryItemDiffs: true,
    useSelectedComponentCategoryItems: true,
  });

  const [filteredComponents, setFilteredComponents] = useState(componentCategoryItems);

  useEffect(() => {
    setFilteredComponents(filterComponentCategoryItems(searchValue, componentCategoryItems));
  }, [componentCategoryItems, searchValue]);

  const headers = useMemo(
    () =>
      Array.from(new Set(componentCategoryItems.flatMap((row) => Object.keys(row))))
        .filter((column: string) =>
          [PricingCalculationColumns, [DisplayColumns.Label], [RegionPricingColumns.Price]]
            .flatMap((e) => Object.values(e))
            .some((v) => v === column),
        )
        .map((column: string) => {
          let label = compoundCaseToTitleCase(column);
          if (Object.values(PricingCalculationColumns).some((v) => v === column)) {
            label = t(I18nKeys.PricingCalculationField);
          }
          return {
            i18nKey: label,
            property: column,
          };
        }),
    [componentCategoryItems, t],
  );

  const getCellClass = useCallback(
    (row: any): string => {
      const classList: string[] = [classes.cell];

      const diff = componentCategoryItemDiffs
        .flatMap(({ changes }) => changes || [])
        .find((d) => d.rowId === row[ClientDataFixedColumns.RowId]);

      if (
        diff &&
        Object.keys(row).some((column) => {
          const columnDiff = getRowDiffColumnToFromValues(diff, column);
          return areDiffValuesDifferent(columnDiff?.from, columnDiff?.to);
        })
      ) {
        classList.push(classes.modified);
      }

      return classList.join(' ');
    },
    [componentCategoryItemDiffs, classes, componentCategoryItems],
  );

  const rows = useMemo(
    () =>
      filteredComponents
        .map((row) =>
          Object.fromEntries(
            Object.keys(row).map((column) => [
              column,
              getDefaultParsedComponentPricingColumnValue(column as keyof ComponentCategoryItem, row),
            ]),
          ),
        )
        .map((row) => ({
          ...row,
          // The displayed price is the region specific price (if any) or the default price
          [RegionPricingColumns.Price]: row.price,
        })),
    [filteredComponents],
  );

  const [sortState, setSortState] = React.useState<SortProperty[]>([]);

  const handleSort = (sortProperties: SortProperty[]): void => {
    sortRows(rows, sortProperties);
    setSortState(sortProperties);
  };

  return (
    <Grid container className={classes.content} spacing={1}>
      <Grid item xs={12} className={classes.tableGrid}>
        <Table
          headers={headers}
          rows={rows}
          loading={false}
          sortProperties={sortState}
          sortableProperties={headers}
          onSort={handleSort}
          handleRowClick={(row): void => {
            dispatch(setSelectedComponentId(row[ClientDataFixedColumns.RowId]));
            dispatch(openDialog({ dialog: Dialogs.PricingComponentEdit }));
          }}
          getCellClass={getCellClass}
          getHeaderClass={() => classes.cell}
        />
      </Grid>
    </Grid>
  );
};
